import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    Pagination,
    Alert,
    CircularProgress,
    Stack,
    Fab,
    useTheme,
    useMediaQuery,
    Collapse,
    InputAdornment,
    Divider,
    Tooltip,
    ImageList,
    ImageListItem,
    Drawer
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Cancel as CancelIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    Close as CloseIcon,
    Clear as ClearIcon,
    LocalShipping as ShippingIcon,
    CheckCircle as CheckCircleIcon,
    Print as PrintIcon
} from '@mui/icons-material';

const BASE_URL = 'https://dhahabjw.com';

// Order Card Component
const OrderCard = ({ order, onView, onEdit, onCancel }) => {
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KW', {
            style: 'currency',
            currency: 'KWD'
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const statusColors = {
        pending: 'warning',
        confirmed: 'info',
        processing: 'primary',
        shipped: 'secondary',
        delivered: 'success',
        cancelled: 'error',
        refunded: 'default'
    };

    const deliveryTypes = {
        home_delivery: 'Delivery',
        store_pickup: 'Pickup'
    };

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {order.orderNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {order.userId?.profile?.firstName} {order.userId?.profile?.familyName}
                        </Typography>
                    </Box>
                    <Chip
                        label={order.status}
                        size="small"
                        color={statusColors[order.status]}
                        sx={{ textTransform: 'capitalize' }}
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Total: {formatCurrency(order.pricing.totalAmount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Delivery Type: {deliveryTypes[order.delivery?.option] || 'N/A'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {formatDate(order.createdAt)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                            <IconButton size="medium" onClick={() => onView(order)}>
                                <ViewIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Status">
                            <IconButton size="medium" onClick={() => onEdit(order)}>
                                <EditIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                        {!['delivered', 'cancelled', 'refunded'].includes(order.status) && (
                            <Tooltip title="Cancel Order">
                                <IconButton size="medium" color="error" onClick={() => onCancel(order)}>
                                    <CancelIcon fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const Orders = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [viewMode, setViewMode] = useState(isMobile ? 'card' : 'table');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalOrders, setTotalOrders] = useState(0);
    const [orderStats, setOrderStats] = useState([]);
    const [filters, setFilters] = useState({
        status: '',
        startDate: '',
        endDate: '',
        userId: '',
        orderNumber: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [viewOrderDialog, setViewOrderDialog] = useState(false);
    const [statusDialog, setStatusDialog] = useState(false);
    const [cancelDialog, setCancelDialog] = useState(false);
    const [invoiceDialog, setInvoiceDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [statusUpdate, setStatusUpdate] = useState({
        status: '',
        trackingNumber: '',
        notes: ''
    });
    const [cancelReason, setCancelReason] = useState('');

    const statusColors = {
        pending: 'warning',
        confirmed: 'info',
        processing: 'primary',
        shipped: 'secondary',
        delivered: 'success',
        cancelled: 'error',
        refunded: 'default'
    };

    const deliveryTypes = {
        home_delivery: 'Delivery',
        store_pickup: 'Pickup'
    };

    useEffect(() => {
        setViewMode(isMobile ? 'card' : 'table');
    }, [isMobile]);

    // API calls
    const fetchOrders = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page,
                limit: 20,
                ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            });

            const response = await fetch(`${BASE_URL}/api/admin/orders?${params}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                },
            });

            const data = await response.json();
            if (data.success) {
                setOrders(data.data);
                setTotalPages(data.pagination?.pages || 1);
                setTotalOrders(data.pagination.total);
                setOrderStats(data.stats);
                setError('');
            } else {
                throw new Error(data.error || 'Failed to fetch orders');
            }
        } catch (err) {
            setError('Network error while fetching orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchOrderDetails = async (orderNumber) => {
        try {
            const response = await fetch(`${BASE_URL}/api/admin/orders/${orderNumber}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                },
            });

            const data = await response.json();
            if (data.success) {
                setSelectedOrder(data.data);
                setViewOrderDialog(true);
            } else {
                setError(data.error || 'Failed to fetch order details');
            }
        } catch (err) {
            setError('Network error while fetching order details');
        }
    };

    const updateOrderStatus = async (statusUpdate) => {
        try {
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Authorization", `Bearer ${localStorage.getItem('adminToken')}`);

            const raw = JSON.stringify(statusUpdate);

            const response = await fetch(`${BASE_URL}/api/admin/orders/${selectedOrder.orderNumber}/status`, {
                method: "PUT",
                headers: myHeaders,
                body: raw
            });
            const data = await response.json();
            if (data.success) {
                setSuccess('Order status updated successfully');
                setStatusDialog(false);
                setStatusUpdate({ status: '', trackingNumber: '', notes: '' });
                fetchOrders();
            } else {
                setError(data.error || 'Failed to update order status');
            }
        } catch (err) {
            setError('Network error while updating order status');
        }
    };

    const cancelOrder = async () => {
        try {
            const response = await fetch(`${BASE_URL}/api/admin/orders/${selectedOrder.orderNumber}/cancel`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reason: cancelReason })
            });

            const data = await response.json();
            if (data.success) {
                setSuccess('Order cancelled successfully');
                setCancelDialog(false);
                setCancelReason('');
                fetchOrders();
            } else {
                setError(data.error || 'Failed to cancel order');
            }
        } catch (err) {
            setError('Network error while cancelling order');
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [page, filters]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            startDate: '',
            endDate: '',
            userId: '',
            orderNumber: '',
            sortBy: 'createdAt',
            sortOrder: 'desc'
        });
        setPage(1);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KW', {
            style: 'currency',
            currency: 'KWD'
        }).format(amount);
    };

    const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== 'createdAt' && value !== 'desc');

    const handleDoubleClick = (order) => {
        fetchOrderDetails(order.orderNumber);
    };

    const generateInvoice = () => {
        setInvoiceDialog(true);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 3
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Orders Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage and track customer orders
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {!isSmallMobile && (
                        <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <IconButton
                                onClick={() => setViewMode('table')}
                                color={viewMode === 'table' ? 'primary' : 'default'}
                                sx={{ borderRadius: 0 }}
                            >
                                <ViewListIcon />
                            </IconButton>
                            <IconButton
                                onClick={() => setViewMode('card')}
                                color={viewMode === 'card' ? 'primary' : 'default'}
                                sx={{ borderRadius: 0 }}
                            >
                                <ViewModuleIcon />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Alerts */}
            <Collapse in={!!error}>
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Collapse>

            <Collapse in={!!success}>
                <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            </Collapse>

            {/* Mobile Filters Drawer */}
            {isSmallMobile && (
                <Drawer
                    anchor="bottom"
                    open={filtersOpen}
                    onClose={() => setFiltersOpen(false)}
                    PaperProps={{
                        sx: { borderRadius: '16px 16px 0 0' }
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h6">Filters</Typography>
                            <IconButton onClick={() => setFiltersOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Search order number..."
                                value={filters.orderNumber}
                                onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                                }}
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="confirmed">Confirmed</MenuItem>
                                    <MenuItem value="processing">Processing</MenuItem>
                                    <MenuItem value="shipped">Shipped</MenuItem>
                                    <MenuItem value="delivered">Delivered</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                    <MenuItem value="refunded">Refunded</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                size="small"
                                label="Start Date"
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextField
                                fullWidth
                                size="small"
                                label="End Date"
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                    label="Sort By"
                                >
                                    <MenuItem value="createdAt">Created Date</MenuItem>
                                    <MenuItem value="updatedAt">Updated Date</MenuItem>
                                    <MenuItem value="pricing.totalAmount">Total Amount</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={filters.sortOrder}
                                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                                    label="Sort Order"
                                >
                                    <MenuItem value="asc">Ascending</MenuItem>
                                    <MenuItem value="desc">Descending</MenuItem>
                                </Select>
                            </FormControl>

                            {hasActiveFilters && (
                                <Button variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}>
                                    Clear All Filters
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Drawer>
            )}

            {/* Desktop Filters */}
            {!isSmallMobile && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterIcon /> Filters
                        </Typography>
                        {hasActiveFilters && (
                            <Button variant="outlined" onClick={clearFilters} size="small" startIcon={<ClearIcon />}>
                                Clear All
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="Search order number..."
                                value={filters.orderNumber}
                                onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="confirmed">Confirmed</MenuItem>
                                    <MenuItem value="processing">Processing</MenuItem>
                                    <MenuItem value="shipped">Shipped</MenuItem>
                                    <MenuItem value="delivered">Delivered</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                    <MenuItem value="refunded">Refunded</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Start Date"
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="End Date"
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                    label="Sort By"
                                >
                                    <MenuItem value="createdAt">Created Date</MenuItem>
                                    <MenuItem value="updatedAt">Updated Date</MenuItem>
                                    <MenuItem value="pricing.totalAmount">Total Amount</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={filters.sortOrder}
                                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                                    label="Sort Order"
                                >
                                    <MenuItem value="asc">Ascending</MenuItem>
                                    <MenuItem value="desc">Descending</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Orders Content */}
            <Paper>
                <Box sx={{ p: 3, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Orders ({totalOrders})</Typography>
                    {isSmallMobile && (
                        <Button
                            variant="outlined"
                            onClick={() => setFiltersOpen(true)}
                            startIcon={<FilterIcon />}
                            size="small"
                        >
                            Filters
                        </Button>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {viewMode === 'table' && !isMobile ? (
                            <TableContainer>
                                <Table onDoubleClick={(event) => {
                                    const row = event.target.closest('tr');
                                    if (row) {
                                        const orderNumber = row.cells[0].textContent;
                                        const order = orders.find(o => o.orderNumber === orderNumber);
                                        if (order) handleDoubleClick(order);
                                    }
                                }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Order Number</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Total Amount</TableCell>
                                            <TableCell>Delivery Type</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orders.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                                        No orders found
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {hasActiveFilters ? 'Try adjusting your filters' : 'No orders available'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            orders.map((order) => (
                                                <TableRow key={order._id} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <ShippingIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                {order.orderNumber}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {order.userId?.profile?.firstName} {order.userId?.profile?.familyName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {order.userId?.email}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={order.status}
                                                            color={statusColors[order.status]}
                                                            size="small"
                                                            sx={{ textTransform: 'capitalize' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {formatCurrency(order.pricing.totalAmount)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {deliveryTypes[order.delivery?.option] || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {formatDate(order.createdAt)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Tooltip title="View Details">
                                                                <IconButton size="large" onClick={() => fetchOrderDetails(order.orderNumber)}>
                                                                    <ViewIcon fontSize="large" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Update Status">
                                                                <IconButton size="large" onClick={async () => {
                                                                    try {
                                                                        const response = await fetch(`${BASE_URL}/api/admin/orders/${order.orderNumber}`, {
                                                                            headers: {
                                                                                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                                                                            },
                                                                        });

                                                                        const data = await response.json();
                                                                        if (data.success) {
                                                                            setSelectedOrder(data.data);
                                                                            setStatusUpdate({ status: data.data.status, trackingNumber: '', notes: '' });
                                                                            setStatusDialog(true);
                                                                        } else {
                                                                            setError(data.error || 'Failed to fetch order details');
                                                                        }
                                                                    } catch (err) {
                                                                        setError('Network error while fetching order details');
                                                                    }
                                                                }}>
                                                                    <EditIcon fontSize="large" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!['delivered', 'cancelled', 'refunded'].includes(order.status) && (
                                                                <Tooltip title="Cancel Order">
                                                                    <IconButton size="large" color="error" onClick={() => {
                                                                        setSelectedOrder(order);
                                                                        setCancelDialog(true);
                                                                    }}>
                                                                        <CancelIcon fontSize="large" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{ p: 3 }}>
                                {orders.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 8 }}>
                                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                            No orders found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {hasActiveFilters ? 'Try adjusting your filters' : 'No orders available'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Grid container spacing={2}>
                                        {orders.map((order) => (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={order._id} onDoubleClick={() => handleDoubleClick(order)}>
                                                <OrderCard
                                                    order={order}
                                                    onView={() => fetchOrderDetails(order.orderNumber)}
                                                    onEdit={() => {
                                                        setSelectedOrder(order);
                                                        setStatusUpdate({ status: order.status, trackingNumber: '', notes: '' });
                                                        setStatusDialog(true);
                                                    }}
                                                    onCancel={() => {
                                                        setSelectedOrder(order);
                                                        setCancelDialog(true);
                                                    }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Box>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(event, value) => setPage(value)}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            {/* Order Details Dialog */}
            <Dialog open={viewOrderDialog} onClose={() => setViewOrderDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Order Details - {selectedOrder?.orderNumber}
                </DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>Customer Information</Typography>
                                <Typography><strong>Name:</strong> {selectedOrder.userId?.profile?.firstName} {selectedOrder.userId?.profile?.familyName}</Typography>
                                <Typography><strong>Email:</strong> {selectedOrder.userId?.email}</Typography>
                                <Typography><strong>Phone:</strong> {selectedOrder.userId?.profile?.phone}</Typography>
                                <Typography><strong>Civil ID:</strong> {selectedOrder.userId?.profile?.civilId}</Typography>
                                <Typography variant="h6" gutterBottom>Civil ID Front Image</Typography>
                                <ImageList sx={{ width: '100%', height: 200 }} cols={1} rowHeight={164}>
                                    <ImageListItem>
                                        <img
                                            src={`${BASE_URL}${selectedOrder.userId?.profile?.civilIdImages.front}`}
                                            alt="Civil ID Front"
                                            loading="lazy"
                                            style={{ objectFit: 'cover' }}
                                        />
                                    </ImageListItem>
                                </ImageList>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>Order Information</Typography>
                                <Typography><strong>Status:</strong>
                                    <Chip
                                        label={selectedOrder.status}
                                        color={statusColors[selectedOrder.status]}
                                        size="small"
                                        sx={{ ml: 1, textTransform: 'capitalize' }}
                                    />
                                </Typography>
                                <Typography><strong>Created:</strong> {formatDate(selectedOrder.createdAt)}</Typography>
                                <Typography><strong>Updated:</strong> {formatDate(selectedOrder.updatedAt)}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>Delivery Information</Typography>
                                <Typography><strong>Delivery Type:</strong> {deliveryTypes[selectedOrder.delivery?.option]}</Typography>
                                {selectedOrder.delivery.option === 'home_delivery' ? (
                                    <>
                                        <Typography variant="body2"><strong>Address Type:</strong> {selectedOrder.delivery.address.type}</Typography>
                                        <Typography variant="body2"><strong>Area:</strong> {selectedOrder.delivery.address.area?.name?.en}</Typography>
                                        {selectedOrder.delivery.address.buildingNumber && (
                                            <Typography variant="body2"><strong>Building Number:</strong> {selectedOrder.delivery.address.buildingNumber}</Typography>
                                        )}
                                        {selectedOrder.delivery.address.streetNumber && (
                                            <Typography variant="body2"><strong>Street Number:</strong> {selectedOrder.delivery.address.streetNumber}</Typography>
                                        )}
                                        {selectedOrder.delivery.address.streetName && (
                                            <Typography variant="body2"><strong>Street Name:</strong> {selectedOrder.delivery.address.streetName}</Typography>
                                        )}
                                        {selectedOrder.delivery.address.block && (
                                            <Typography variant="body2"><strong>Block:</strong> {selectedOrder.delivery.address.block}</Typography>
                                        )}
                                        {selectedOrder.delivery.address.avenue && (
                                            <Typography variant="body2"><strong>Avenue:</strong> {selectedOrder.delivery.address.avenue}</Typography>
                                        )}
                                        {selectedOrder.delivery.address.floor && (
                                            <Typography variant="body2"><strong>Floor:</strong> {selectedOrder.delivery.address.floor}</Typography>
                                        )}
                                        {selectedOrder.delivery.address.apartmentNumber && (
                                            <Typography variant="body2"><strong>Apartment Number:</strong> {selectedOrder.delivery.address.apartmentNumber}</Typography>
                                        )}
                                        {selectedOrder.delivery.address.company && (
                                            <Typography variant="body2"><strong>Company:</strong> {selectedOrder.delivery.address.company}</Typography>
                                        )}
                                        {selectedOrder.delivery.address.additionalDirections && (
                                            <Typography variant="body2"><strong>Directions:</strong> {selectedOrder.delivery.address.additionalDirections}</Typography>
                                        )}
                                        <Typography variant="body2"><strong>Phone:</strong> {selectedOrder.delivery.address.phone}</Typography>
                                        {selectedOrder.delivery.address.addressLabel && (
                                            <Typography variant="body2"><strong>Label:</strong> {selectedOrder.delivery.address.addressLabel}</Typography>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="subtitle1">Delivery Type: Store Pickup</Typography>
                                        <Typography variant="body2"><strong>Store Location:</strong> {selectedOrder.delivery.storeLocation}</Typography>
                                        <Typography variant="body2"><strong>Contact Phone:</strong> {selectedOrder.delivery.pickupContactPhone}</Typography>
                                    </>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>Order Items</Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Product</TableCell>
                                                <TableCell align="right">Quantity</TableCell>
                                                <TableCell align="right">Unit Price</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedOrder.items?.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{item.productName}</TableCell>
                                                    <TableCell align="right">{item.quantity}</TableCell>
                                                    <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(item.totalPrice)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>Pricing Summary</Typography>
                                <Typography><strong>Subtotal:</strong> {formatCurrency(selectedOrder.pricing.subtotal)}</Typography>
                                <Typography><strong>Shipping:</strong> {formatCurrency(selectedOrder.pricing.shipping)}</Typography>
                                <Typography><strong>Discount:</strong> {formatCurrency(selectedOrder.pricing.discount)}</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="h6"><strong>Total:</strong> {formatCurrency(selectedOrder.pricing.totalAmount)}</Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>Payment Information</Typography>
                                <Typography><strong>Method:</strong> {selectedOrder.payment?.method?.toUpperCase()}</Typography>
                                <Typography><strong>Status:</strong>
                                    <Chip
                                        label={selectedOrder.payment?.status}
                                        color={selectedOrder.payment?.status === 'completed' ? 'success' : 'warning'}
                                        size="small"
                                        sx={{ ml: 1, textTransform: 'capitalize' }}
                                    />
                                </Typography>
                                {selectedOrder.payment?.transactionId && (
                                    <Typography><strong>Transaction ID:</strong> {selectedOrder.payment.transactionId}</Typography>
                                )}
                            </Grid>

                            {selectedOrder.notes && (
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>Notes</Typography>
                                    <Typography variant="body2">{selectedOrder.notes}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewOrderDialog(false)}>
                        Close
                    </Button>
                    <Button variant="contained" onClick={generateInvoice}>
                        Generate Invoice
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Status Update Dialog */}
            <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Order Status</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusUpdate.status}
                                    label="Status"
                                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="confirmed">Confirmed</MenuItem>
                                    <MenuItem value="processing">Processing</MenuItem>
                                    <MenuItem value="shipped">Shipped</MenuItem>
                                    <MenuItem value="delivered">Delivered</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                    <MenuItem value="refunded">Refunded</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {statusUpdate.status === 'shipped' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Tracking Number"
                                    value={statusUpdate.trackingNumber}
                                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, trackingNumber: e.target.value }))}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Notes (Optional)"
                                multiline
                                rows={3}
                                value={statusUpdate.notes}
                                onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => updateOrderStatus(statusUpdate)}
                        variant="contained"
                        disabled={!statusUpdate.status}
                    >
                        Update Status
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Order Dialog */}
            <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Cancel Order</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Are you sure you want to cancel order {selectedOrder?.orderNumber}?
                    </Typography>
                    <TextField
                        fullWidth
                        label="Cancellation Reason"
                        multiline
                        rows={3}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={cancelOrder}
                        variant="contained"
                        color="error"
                        disabled={!cancelReason.trim()}
                    >
                        Cancel Order
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Invoice Dialog */}
            <Dialog open={invoiceDialog} onClose={() => setInvoiceDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ '@media print': { display: 'none' } }}>
                    Invoice - {selectedOrder?.orderNumber}
                    <Button onClick={handlePrint} sx={{ ml: 2, '@media print': { display: 'none' } }} variant="contained" startIcon={<PrintIcon />}>
                        Print
                    </Button>
                </DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Typography variant="h4">DHAHAB JEWELERY</Typography>
                                <Typography variant="body1">Mubarkiya, Al-Raya Gold Center, First Floor, Shop 18</Typography>
                                <Typography variant="body1">Phone: +96566111290</Typography>
                                <Typography variant="body1">Email: info@dhahabjw.com</Typography>
                                <Typography variant="body1">Date: {formatDate(new Date())}</Typography>
                                <Typography variant="body1">Invoice No: {selectedOrder.orderNumber}</Typography>
                            </Box>

                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6">Customer Information</Typography>
                                <Typography><strong>Name:</strong> {selectedOrder.userId?.profile?.firstName} {selectedOrder.userId?.profile?.familyName}</Typography>
                                <Typography><strong>Civil ID:</strong> {selectedOrder.userId?.profile?.civilId}</Typography>
                                <img
                                    src={`${BASE_URL}${selectedOrder.userId?.profile?.civilIdImages.front}`}
                                    alt="Civil ID Front"
                                    loading="lazy"
                                    style={{ objectFit: 'cover', height: 200 }}
                                />
                            </Box>

                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            <TableCell align="right">Gold Rate</TableCell>
                                            <TableCell align="right">Making</TableCell>
                                            <TableCell align="right">Unit Price</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedOrder.items?.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.productName}</TableCell>
                                                <TableCell align="right">{item.quantity}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.metalPrice)}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.makingCharges)}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.totalPrice)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ mt: 4, textAlign: 'right' }}>
                                <Typography><strong>Subtotal:</strong> {formatCurrency(selectedOrder.pricing.subtotal)}</Typography>
                                <Typography><strong>Shipping:</strong> {formatCurrency(selectedOrder.pricing.shipping)}</Typography>
                                <Typography><strong>Discount:</strong> {formatCurrency(selectedOrder.pricing.discount)}</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="h6"><strong>Total Amount:</strong> {formatCurrency(selectedOrder.pricing.totalAmount)}</Typography>
                            </Box>

                            <Box sx={{ mt: 4, textAlign: 'center' }}>
                                <Typography variant="body1">Thank you for your business!</Typography>
                                <Typography variant="body2">Dhahab Jewelry, Alrai, Kuwait</Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ '@media print': { display: 'none' } }}>
                    <Button onClick={() => setInvoiceDialog(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Orders;